import { get } from 'lodash';
import { DAYS, POSTS } from '../../pages/allAnalytics/constants';
import {
  BRIEF_PLATFORM_DISPLAY_NAME_KEYS,
  PLATFORM_COLORS,
  PLATFORM_DISPLAY_NAME_KEYS_WITH_ALL
} from '@/config/icons';
import { data } from '@/pages/dashboard/demoDataDocument';

export const getFirstNotNullValue = (
  dataArray: number[],
  startIndex: number,
  endIndex: number,
  step: number
) => {
  if (!dataArray || !Array.isArray(dataArray)) {
    return 0;
  }

  for (
    let i = startIndex;
    step > 0 ? i < endIndex && i < dataArray.length : i > endIndex && i >= 0;
    i += step
  ) {
    if (dataArray[i] !== null && dataArray[i] !== undefined) {
      return dataArray[i];
    }
  }
  return 0;
};

const hasNonNullValue = (values?: Array<number | null | undefined>) =>
  Array.isArray(values) && values.some((v) => v !== null && v !== undefined);

export const getFirstAndLastValues = (
  dataArray: number[],
  oldDataArray?: number[]
) => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) {
    return { previous: 0, current: 0 };
  }

  // For followers (cumulative data), we want the LAST non-null value from each array
  const previous = getFirstNotNullValue(
    oldDataArray || [],
    (oldDataArray?.length || 1) - 1,
    -1,
    -1
  );
  const current = getFirstNotNullValue(dataArray, dataArray.length - 1, -1, -1);

  return { previous, current };
};

export const getLastNotNullValue = (dataArray: number[]) => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) {
    return 0;
  }
  for (let i = dataArray.length - 1; i >= 0; i--) {
    if (dataArray[i] !== null && dataArray[i] !== undefined) {
      return dataArray[i];
    }
  }
  return 0; // Return 0 if no valid value is found
};
const getFirstNonNull = (
  values?: Array<number | null | undefined>,
  direction: 'forward' | 'backward' = 'forward'
): number | null => {
  if (!Array.isArray(values) || values.length === 0) return null;
  if (direction === 'forward') {
    for (let i = 0; i < values.length; i++) {
      if (values[i] !== null && values[i] !== undefined) return values[i]!;
    }
  } else {
    for (let i = values.length - 1; i >= 0; i--) {
      if (values[i] !== null && values[i] !== undefined) return values[i]!;
    }
  }
  return null;
};export const getDifferenceBetweenFirstAndLastValues = (
  dataArray: Array<number | null>,
  oldData?: Array<number | null>
): { previous: number | null; current: number | null } => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) {
    return { previous: null, current: null };
  }

  const startCurrent = getFirstNonNull(dataArray, 'forward');
  const endCurrent = getFirstNonNull(dataArray, 'backward');
  if (startCurrent === null || endCurrent === null) {
    return { previous: null, current: null };
  }

  if (!hasNonNullValue(oldData)) {
    return { previous: null, current: null };
  }

  const startOld = getFirstNonNull(oldData, 'forward');
  const endOld = getFirstNonNull(oldData, 'backward');
  if (startOld === null || endOld === null) {
    return { previous: null, current: null };
  }

  return {
    previous: endOld - startOld,
    current: endCurrent - startCurrent
  };
};

export const getDifferenceBetweenSums = (
  dataArray: number[],
  oldData?: number[]
) => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) {
    return { previous: 0, current: 0 };
  }
  const current = dataArray.reduce((acc, value) => acc + (value || 0), 0);
  const previous = oldData
    ? oldData.reduce((acc, value) => acc + (value || 0), 0)
    : current; // Calculate previous if oldData is provided
  const difference = current - previous;
  return { previous, current: difference };
};

export const sumOfValues = (dataArray: number[]) => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) {
    return 0;
  }
  return dataArray.reduce((acc, value) => acc + (value || 0), 0);
};
export const getsumOfValues = (dataArray: number[], oldData?: number[]) => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) {
    return { previous: 0, current: 0 };
  }
  const current = dataArray.reduce((acc, value) => acc + (value || 0), 0);
  const previous = oldData
    ? oldData.reduce((acc, value) => acc + (value || 0), 0)
    : current; // Calculate previous if oldData is provided
  return { previous, current };
};

function getFirstNotNullNeighbor(
  values: (number | null)[],
  index: number
): number | null {
  // Look left
  for (let i = index - 1; i >= 0; i--) {
    if (values[i] !== null && values[i] !== undefined) return values[i]!;
  }
  // Look right
  for (let i = index + 1; i < values.length; i++) {
    if (values[i] !== null && values[i] !== undefined) return values[i]!;
  }
  return null;
}

export const getGrowthMetricsEntries = (
  data: any,
  oldData: any,
  config: any[],
  t: any,
  useLastValue: boolean = false
) => {
  const entries = config.map((entry, index) => {
    const plotData = data[entry.plotName];
    const oldPlotData = oldData[entry.plotName] || {};

    if (!plotData) {
      return null;
    }

    let dataArray: Array<number | null> = [];
    let oldDataArray: Array<number | null> = [];
    let hasPreviousData = false;

    if (entry.sumMetrics) {
      const currentArrays = Object.values(plotData)
        .filter((metric: any) => Array.isArray(metric?.values))
        .map((metric: any) => {
          const values = metric.values;
          const hasData = hasNonNullValue(values);
          return values.map((v, i) => {
            if (v != null) return v;
            if (useLastValue && hasData) {
              const neighbor = getFirstNotNullNeighbor(values, i);
              return neighbor;
            }
            return null;
          });
        });

      let hasPreviousDataForSum = false;
      const oldArrays = Object.values(oldPlotData)
        .filter((metric: any) => Array.isArray(metric?.values))
        .map((metric: any) => {
          const values = metric.values;
          const hasData = hasNonNullValue(values);
          if (!hasPreviousDataForSum && hasNonNullValue(values)) {
            hasPreviousDataForSum = true;
          }
          return values.map((v, i) => {
            if (v != null) return v;
            if (useLastValue && hasData) {
              const neighbor = getFirstNotNullNeighbor(values, i);
              return neighbor;
            }
            return null;
          });
        });

      hasPreviousData = hasPreviousDataForSum;

      const maxLength = Math.max(...currentArrays.map((arr) => arr.length));

      dataArray = Array.from({ length: maxLength }, (_, i) => {
        const hasValue = currentArrays.some((arr) => arr[i] != null);
        if (!hasValue) return null;
        return currentArrays.reduce((sum, arr) => sum + (arr[i] ?? 0), 0);
      });

      oldDataArray =
        oldArrays.length === 0
          ? []
          : Array.from({ length: maxLength }, (_, i) => {
              const hasValue = oldArrays.some((arr) => arr[i] != null);
              if (!hasValue) return null;
              return oldArrays.reduce((sum, arr) => sum + (arr[i] ?? 0), 0);
            });
    } else {
      if (entry.metricName && plotData[entry.metricName]) {
        const values = plotData[entry.metricName].values || [];
        const oldValues = oldPlotData[entry.metricName]?.values || [];
        const hasDataCurrent = hasNonNullValue(values);
        const hasDataOld = hasNonNullValue(oldValues);

        dataArray = values.map((v) => {
          if (v != null) return v;
          if (useLastValue && hasDataCurrent) {
            const last = getLastNotNullValue(values);
            return last;
          }
          return null;
        });

        oldDataArray = oldValues.map((v) => {
          if (v != null) return v;
          if (useLastValue && hasDataOld) {
            const last = getLastNotNullValue(oldValues);
            return last;
          }
          return null;
        });

        hasPreviousData = hasNonNullValue(oldValues);
      } else {
        return null;
      }
    }

    let { previous, current } = entry.calculateFunction(
      dataArray,
      oldDataArray
    );

    const hasNumericPrevious =
      typeof previous === 'number' && !Number.isNaN(previous);
    const hasNumericCurrent =
      typeof current === 'number' && !Number.isNaN(current);

    if (entry.divideBy && hasNumericCurrent && hasNumericPrevious) {
      if (entry.divideBy === DAYS) {
        const days = dataArray.length || 1;
        current = Math.round((current / days) * 100) / 100;
        previous = Math.round((previous / days) * 100) / 100;
      }
      if (entry.divideBy === POSTS) {
        const posts =
          data['posts_per_day_plot']?.['posts_per_day_plot']?.values || [];
        const totalPosts =
          posts.reduce((acc, value) => acc + (value || 0), 0) || 1;
        current = Math.round((current / totalPosts) * 100) / 100;
        previous = Math.round((previous / totalPosts) * 100) / 100;
      }
    }

    return {
      title: t(entry.title),
      helpText: t(entry.helpText),
      cssIcon: entry.cssIcon,
      cssIconColor: entry.cssIconColor,
      current: current,
      previous: previous,
      nullDataTooltipText: t('platform_analytics.null_data_tooltip'),
      showGrowthBadge:
        (entry.showGrowthBadge || false) &&
        hasPreviousData &&
        hasNumericPrevious &&
        hasNumericCurrent
    };
  });

  const filteredEntries = entries.filter((entry) => entry !== null);

  return filteredEntries;
};

export const getDoughnutChartData = (
  data: any,
  config: {
    plotName: string;
    metricName?: string;
    colors: { [key: string]: string }; // colors mapping platform key to hex code
    title: string; // Chart title
    getFinalValue: any;
    doughnutChartTitle: any;
  },
  t
): {
  title: string;
  data: { value: number; name: string; color: string }[];
} => {
  const plotData = data[config.plotName];
  if (!plotData) {
    return {
      title: t(config.doughnutChartTitle || config.title),
      data: []
    };
  }
  // Determine where the platform data is located
  const platformSpecificData = config.metricName
    ? plotData[config.metricName]
    : plotData;
  if (!platformSpecificData) {
    return {
      title: t(config.doughnutChartTitle || config.title),
      data: []
    };
  }

  const chartData = Object.keys(platformSpecificData)
    .map((platformKey) => {
      const platformData = platformSpecificData[platformKey];

      const valuesOverTime = platformData?.values || [];
      // if all values are null or undefined, skip this platform

      const value = platformData.displayValue || 0; // Use displayValue if available, otherwise use 0

      return {
        value: value,
        name: t(BRIEF_PLATFORM_DISPLAY_NAME_KEYS[platformKey]),
        color: PLATFORM_COLORS[platformKey]
      };
    })
    .filter((item) => item.value > 0);

  if (chartData.length === 0) {
    return {
      title: t(config.doughnutChartTitle || config.title),
      data: []
    };
  }

  return {
    title: t(config.doughnutChartTitle || config.title),
    data: chartData
  };
};

/*
{
      "title": "Example Bar Chart",
      "data": [
        {
          "name": "Facebook",
          "value": 1000,
          "color": "#4267B2"
        },
        {
          "name": "Instagram",
          "value": 1200,
          "color": "#E1306C"
        },
        {
          "name": "TikTok",
          "value": 1500,
          "color": "#000000"
        },
        {
          "name": "YouTube",
          "value": 300,
          "color": "#FF0000"
        }
      ],
      "style": {
        "height": 300
      }
    }
      */

export const getBarChartData = (
  data: any,
  config: any,
  t: any,
  oldData: any = {}
): any => {
  const plotData = data[config.plotName];
  if (!plotData) {
    return {
      title: t(config.barChartTitle),
      data: [],
      style: {
        height: config.style?.height || 300
      }
    };
  }

  const chartData = Object.keys(plotData)
    .map((platformKey) => {
      const platformData = plotData[platformKey];
      const valuesOverTime = platformData?.values || [];

      // Skip platform if all values are null or undefined
      if (
        valuesOverTime.every(
          (value: number | null) => value === null || value === undefined
        )
      ) {
        return null;
      }

      let previous = 0;
      let current = 0;

      if (config.useOldData) {
        const oldPlatformData = oldData[config.plotName]?.[platformKey];
        if (!oldPlatformData) {
          return null;
        }

        const oldValuesOverTime = oldPlatformData?.values || [];
        previous = config.getFinalValue(oldValuesOverTime);
        current = config.getFinalValue(valuesOverTime);

        if (previous === 0 && current === 0) {
          return null;
        }
      } else {
        ({ previous, current } = config.GetGrowthValues(valuesOverTime));
      }

      return {
        name: t(PLATFORM_DISPLAY_NAME_KEYS_WITH_ALL[platformKey]),
        value: current - previous,
        color: PLATFORM_COLORS[platformKey]
      };
    })
    .filter((item) => item !== null);

  if (chartData.length === 0) {
    return {
      title: t(config.barChartTitle),
      data: [],
      style: {
        height: config.style?.height || 300
      }
    };
  }

  return {
    title: t(config.barChartTitle),
    data: chartData,
    style: {
      height: config.style?.height || 300
    }
  };
};

const calculateGrowthRate = (current: number, previous: number): number => {
  if (previous === 0) {
    return current > 0 ? 100 : 0; // If previous is 0, return 100% growth if current is positive, otherwise 0%
  }
  return ((current - previous) / previous) * 100; // Calculate growth rate as a percentage
};

export const getTableDataForReach = (
  data: any,
  oldData: any,
  config: any,
  t: any
) => {
  const plotData = data[config.plotName];
  if (!plotData) {
    return {
      title: t(config.tableChartTitle),
      columns: config.tableChartColumns.map((column: any) => ({
        key: column.key,
        label: t(column.label),
        align: column.align,
        isNumeric: column.isNumeric || false,
        showGrowthArrow: column.showGrowthArrow || false,
        blurGrowthIndicator: column.blurGrowthIndicator || false,
        showPercentage: column.showPercentage || false
      })),
      rows: [],
      showSumRow: config.showSumRow || true
    };
  }
  const oldPlotData = oldData[config.plotName];
  const rows = Object.keys(plotData).map((platformKey) => {
    const platformData = plotData[platformKey];

    const valuesOverTime = platformData?.values || [];
    if (
      valuesOverTime.every(
        (value: number | null) => value === null || value === undefined
      )
    ) {
      return null;
    }

    const oldValuesOverTime = oldPlotData[platformKey]?.values || [];
    const current = config.GetGrowthValues(valuesOverTime);
    const previous = config.GetGrowthValues(oldValuesOverTime);
    const row: any = {
      platform: t(PLATFORM_DISPLAY_NAME_KEYS_WITH_ALL[platformKey]),
      metric_name: current,
      growth_metric_name: {
        current: current,
        previous: previous
      },
      metric_growth_name: current - previous,
      growth_metric_growth_name: {
        current: current,
        previous: previous
      },
      metric_growth_name_rate:
        Math.round(calculateGrowthRate(current, previous) * 100) / 100
    };
    return row;
  });

  return {
    title: t(config.tableChartTitle),
    columns: config.tableChartColumns.map((column: any) => ({
      key: column.key,
      label: t(column.label),
      align: column.align,
      isNumeric: column.isNumeric || false,
      showGrowthArrow: column.showGrowthArrow || false,
      blurGrowthIndicator: column.blurGrowthIndicator || false,
      showPercentage: column.showPercentage || false
    })),
    rows: rows
      .filter((row) => row !== null)
      .sort((a, b) => a.platform.localeCompare(b.platform)), // Filter out null rows and sort
    showSumRow: config.showSumRow || true
  };
};
export const getTableChartDataForFollowers = (
  data: any,
  oldData: any,
  config: any,
  t: any
) => {
  const plotData = data[config.plotName];
  if (!plotData) {
    return {
      title: t(config.tableChartTitle),
      columns: config.tableChartColumns.map((column: any) => ({
        key: column.key,
        label: t(column.label),
        align: column.align,
        isNumeric: column.isNumeric || false,
        showGrowthArrow: column.showGrowthArrow || false,
        blurGrowthIndicator: column.blurGrowthIndicator || false,
        showPercentage: column.showPercentage || false
      })),
      rows: [],
      showSumRow: config.showSumRow || true
    };
  }
  const oldPlotData = oldData[config.plotName];
  const rows = Object.keys(plotData).map((platformKey) => {
    const platformData = plotData[platformKey];
    const valuesOverTime = platformData?.values || [];
    if (
      valuesOverTime.every(
        (value: number | null) => value === null || value === undefined
      )
    ) {
      return null;
    }
    const { previous, current } = config.GetGrowthValues(valuesOverTime);
    const oldValuesOverTime = oldPlotData[platformKey]?.values || [];
    const { previous: oldPrevious, current: oldCurrent } =
      config.GetGrowthValues(oldValuesOverTime);

    const row: any = {
      platform: t(PLATFORM_DISPLAY_NAME_KEYS_WITH_ALL[platformKey]),
      followers: current,
      growth_followers: {
        current: current,
        previous: oldCurrent
      },
      metric_growth_followers: current - oldCurrent,
      growth_metric_growth_followers: {
        current: current,
        previous: oldCurrent
      },
      //two digits after decimal point
      metric_growth_followers_rate:
        Math.floor(calculateGrowthRate(current, oldCurrent) * 100) / 100,
      growth_metric_growth_followers_rate: {
        current: calculateGrowthRate(current, oldCurrent),
        previous: calculateGrowthRate(oldCurrent, oldPrevious) // TODO check this if we add this column
      }
    };
    return row;
  });
  return {
    title: t(config.tableChartTitle),
    columns: config.tableChartColumns.map((column: any) => ({
      key: column.key,
      label: t(column.label),
      align: column.align,
      isNumeric: column.isNumeric || false,
      showGrowthArrow: column.showGrowthArrow || false,
      blurGrowthIndicator: column.blurGrowthIndicator || false,
      showPercentage: column.showPercentage || false
    })),
    //sort alphabetically by platform name
    rows: rows
      .filter((row) => row !== null)
      .sort((a, b) => a.platform.localeCompare(b.platform)), // Filter out null rows and sort
    showSumRow: config.showSumRow || true
  };
};

export const getTableChartData = (
  data: any,
  oldData: any,
  config: any,
  t: any
) => {
  if (config.useReachFunction) {
    return getTableDataForReach(data, oldData, config, t);
  }
  return getTableChartDataForFollowers(data, oldData, config, t);
};

export const getFirstAndLastValuesForCurrentData = (dataArray: number[]) => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) {
    return { previous: 0, current: 0 };
  }
  const previous = getFirstNotNullValue(dataArray, 0, dataArray.length, 1);
  const current = getFirstNotNullValue(dataArray, dataArray.length - 1, -1, -1);
  return { previous, current };
};
